import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISSED_KEY = "pwa_install_dismissed";

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === "true"; }
    catch { return false; }
  });

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const ua = navigator.userAgent || "";

    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios|edgios/i.test(ua);
    if (isIOS && isSafari) {
      setShowIOS(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      try { localStorage.setItem(DISMISSED_KEY, "true"); } catch {}
      setDeferred(null);
      setDismissed(true);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (dismissed) return null;
  if (!deferred && !showIOS) return null;

  const handleInstall = async () => {
    if (showIOS) {
      window.alert(
        "To install Haven:\n\n" +
        "1. Tap the Share button (square with up-arrow) at the bottom of Safari.\n" +
        "2. Scroll down and tap \u201cAdd to Home Screen\u201d.\n" +
        "3. Tap \u201cAdd\u201d in the top right.\n\n" +
        "Haven will then open like a native app from your home screen."
      );
      handleDismiss();
      return;
    }
    if (!deferred) return;

    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      handleDismiss();
    }
    setDeferred(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISSED_KEY, "true"); } catch {}
  };

  // P3 FIX: Changed bottom-4 to top-20 to avoid overlapping GooglePlayBanner
  return (
    <div className="fixed top-20 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-40 bg-white border border-[#E7E5E0] shadow-xl rounded-2xl p-4 flex items-center gap-3 animate-slide-up">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EC4899] to-[#7C3AED] flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-gray-900">Install Haven</p>
        <p className="text-xs text-gray-500">
          {showIOS
            ? "Add to your home screen for the full app experience."
            : "Install to your home screen for the full app experience."}
        </p>
      </div>
      <button
        onClick={handleInstall}
        className="px-3 py-1.5 bg-[#7C3AED] text-white rounded-lg text-xs font-semibold whitespace-nowrap hover:bg-[#6D28D9] transition-colors"
      >
        {showIOS ? "How" : "Install"}
      </button>
      <button
        onClick={handleDismiss}
        className="p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-gray-400" />
      </button>
    </div>
  );
}